import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDDialog from "components/MDDialog";
import ProdutoImageCropModal from "components/ProdutoImageCropModal";
import { LojaPageContainer } from "layouts/pages/loja/common";
import { setMessage, useMaterialUIController } from "context";
import { debouce } from "helpers/utils";
import {
  PATROCINADOR_LOGO_HEIGHT,
  PATROCINADOR_LOGO_WIDTH,
  validateImageFilesExactDimensions,
} from "helpers/validateImageDimensions";

import { IPatrocinador } from "interfaces/patrocinador";
import {
  createPatrocinador,
  deletePatrocinador,
  desvincularPatrocinadorEvento,
  editPatrocinador,
  getAllPatrocinadores,
  vincularPatrocinadorEvento,
} from "services/patrocinadores";
import { getAllEventos } from "services/evento";
import api from "services/api";

import PatrocinadorFormDialog from "./components/PatrocinadorFormDialog";
import TablePatrocinador from "./components/TablePatrocinador";
import {
  PATROCINADORES_ACCENT,
  patrocinadoresToolbarSx,
  saveButtonSx,
} from "./patrocinadores.theme";

type EventoOption = { id: number; titulo: string };

const EMPTY_FORM = {
  nome: "",
  descricao: "",
  urlSite: "",
  ordem: 0,
  ativo: true,
  exibirHome: true,
};

function StatMini({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: accent ? "rgba(249, 96, 48, 0.3)" : "grey.200",
        bgcolor: accent ? "rgba(249, 96, 48, 0.04)" : "background.paper",
        height: "100%",
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        color={accent ? PATROCINADORES_ACCENT : "text.primary"}
      >
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

export default function Patrocinadores(): JSX.Element {
  const [, dispatch] = useMaterialUIController();
  const [list, setList] = useState<IPatrocinador[]>([]);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState<IPatrocinador[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [selected, setSelected] = useState<IPatrocinador | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoPreviewRef = useRef<string>("");

  const [eventosAtivos, setEventosAtivos] = useState<EventoOption[]>([]);
  const [selectedEventos, setSelectedEventos] = useState<EventoOption[]>([]);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [patrocinadoresCompact, setPatrocinadoresCompact] = useState(false);
  const [loadingSiteSettings, setLoadingSiteSettings] = useState(true);

  const isFiltered = searchResults !== null;
  const displayedList = searchResults ?? list;

  const stats = useMemo(() => {
    const ativos = list.filter((p) => p.ativo).length;
    const naHome = list.filter((p) => p.exibirHome).length;
    return { ativos, naHome };
  }, [list]);

  const loadEventos = async () => {
    if (eventosAtivos.length > 0) return;
    setLoadingEventos(true);
    try {
      const { eventos } = await getAllEventos(1, 1000, "active");
      setEventosAtivos(eventos.map((e) => ({ id: e.id, titulo: e.titulo })));
    } finally {
      setLoadingEventos(false);
    }
  };

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getAllPatrocinadores();
      setList(Array.isArray(data) ? data : []);
    } catch (error: unknown) {
      setList([]);
      setMessage(dispatch, {
        open: true,
        text: error instanceof Error ? error.message : "Erro ao carregar patrocinadores",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setSearchResults(null);
    setSearch("");
  }, [list.length]);

  useEffect(() => {
    let alive = true;
    void api
      .get("/site-settings")
      .then((res) => {
        if (!alive || res.status < 200 || res.status >= 300) return;
        const data = res.data as { patrocinadoresCompact?: boolean };
        if (typeof data.patrocinadoresCompact === "boolean") {
          setPatrocinadoresCompact(data.patrocinadoresCompact);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoadingSiteSettings(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const applySearch = useCallback(
    (term: string) => {
      const q = term.trim().toLowerCase();
      if (!q) {
        setSearchResults(null);
        return;
      }
      setSearchResults(
        list.filter(
          (item) =>
            item.nome?.toLowerCase().includes(q) ||
            item.descricao?.toLowerCase().includes(q),
        ),
      );
    },
    [list],
  );

  const debouncedSearch = useMemo(
    () => debouce(applySearch, 300),
    [applySearch],
  );

  const handlePatrocinadoresCompact = async (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    const previous = patrocinadoresCompact;
    setPatrocinadoresCompact(checked);
    try {
      const res = await api.put("/site-settings", {
        patrocinadoresCompact: checked,
      });
      if (res.status < 200 || res.status >= 300) {
        throw new Error("Resposta inválida");
      }
      setMessage(dispatch, {
        open: true,
        severity: "success",
        text: checked
          ? "Vitrine compacta ativada no site."
          : "Vitrine com cards completos ativada no site.",
      });
    } catch {
      setPatrocinadoresCompact(previous);
      setMessage(dispatch, {
        open: true,
        severity: "error",
        text: "Não foi possível salvar a exibição da vitrine de patrocinadores.",
      });
    }
  };

  const getNextOrder = () => {
    const maxOrder = list.reduce((max, item) => {
      const ordem = Number(item.ordem);
      return Number.isFinite(ordem) && ordem > max ? ordem : max;
    }, 0);
    return maxOrder + 1;
  };

  const resetCropState = () => {
    setCropModalOpen(false);
    setCropFile(null);
  };

  const setLogoPreviewUrl = (url: string) => {
    if (logoPreviewRef.current && logoPreviewRef.current.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreviewRef.current);
    }
    logoPreviewRef.current = url;
    setLogoPreview(url);
  };

  const applyLogoFile = (file: File) => {
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
  };

  const handleOpenCreate = () => {
    setSelected(null);
    setForm({ ...EMPTY_FORM, ordem: getNextOrder() });
    setLogoFile(null);
    setLogoPreviewUrl("");
    resetCropState();
    setSelectedEventos([]);
    void loadEventos();
    setOpenForm(true);
  };

  const handleOpenEdit = (row: IPatrocinador) => {
    setSelected(row);
    setForm({
      nome: row.nome,
      descricao: row.descricao ?? "",
      urlSite: row.urlSite ?? "",
      ordem: row.ordem,
      ativo: row.ativo,
      exibirHome: row.exibirHome,
    });
    setLogoFile(null);
    setLogoPreviewUrl(row.urlLogo ?? "");
    resetCropState();
    setSelectedEventos(
      (row.eventos ?? []).map((e) => ({
        id: e.eventoId,
        titulo: e.evento.titulo,
      })),
    );
    void loadEventos();
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelected(null);
  };

  const handleSelectDelete = (row: IPatrocinador) => {
    setSelected(row);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (!selected) return;
    await deletePatrocinador(selected.id);
    setOpenConfirmDelete(false);
    setSelected(null);
    setMessage(dispatch, {
      open: true,
      text: "Patrocinador excluído com sucesso",
      severity: "success",
    });
    void load();
  };

  const handleLogoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setMessage(dispatch, {
        open: true,
        text: "Selecione um arquivo de imagem válido",
        severity: "warning",
      });
      return;
    }

    const invalid = await validateImageFilesExactDimensions(
      [file],
      PATROCINADOR_LOGO_WIDTH,
      PATROCINADOR_LOGO_HEIGHT,
    );

    if (invalid.length === 0) {
      applyLogoFile(file);
      return;
    }

    setCropFile(file);
    setCropModalOpen(true);
  };

  const handleCropConfirm = (cropped: File) => {
    applyLogoFile(cropped);
    resetCropState();
  };

  const handleCropError = (message: string) => {
    setMessage(dispatch, {
      open: true,
      text: message,
      severity: "error",
    });
  };

  const handleSubmit = async () => {
    if (!form.nome.trim()) {
      setMessage(dispatch, {
        open: true,
        text: "O campo Nome é obrigatório",
        severity: "warning",
      });
      return;
    }

    if (logoFile) {
      const invalidDimensions = await validateImageFilesExactDimensions(
        [logoFile],
        PATROCINADOR_LOGO_WIDTH,
        PATROCINADOR_LOGO_HEIGHT,
      );

      if (invalidDimensions.length > 0) {
        setCropFile(logoFile);
        setCropModalOpen(true);
        setMessage(dispatch, {
          open: true,
          text: `O logo deve ter exatamente ${PATROCINADOR_LOGO_WIDTH} × ${PATROCINADOR_LOGO_HEIGHT} px. Ajuste o recorte antes de salvar.`,
          severity: "warning",
        });
        return;
      }
    }

    const fd = new FormData();
    fd.append("nome", form.nome);
    fd.append("descricao", form.descricao);
    if (form.urlSite) fd.append("urlSite", form.urlSite);
    fd.append("ordem", String(form.ordem));
    fd.append("ativo", String(form.ativo));
    fd.append("exibirHome", String(form.exibirHome));
    if (logoFile) fd.append("logo", logoFile);

    setIsSubmitting(true);
    try {
      if (selected) {
        await editPatrocinador(selected.id, fd);

        const oldIds = (selected.eventos ?? []).map((e) => e.eventoId);
        const newIds = selectedEventos.map((e) => e.id);
        const toAdd = newIds.filter((id) => !oldIds.includes(id));
        const toRemove = oldIds.filter((id) => !newIds.includes(id));
        await Promise.all([
          ...toAdd.map((id) => vincularPatrocinadorEvento(selected.id, id, 0)),
          ...toRemove.map((id) => desvincularPatrocinadorEvento(selected.id, id)),
        ]);
      } else {
        const { data: novoPat } = await createPatrocinador(fd);
        if (novoPat?.id && selectedEventos.length > 0) {
          await Promise.all(
            selectedEventos.map((e) => vincularPatrocinadorEvento(novoPat.id, e.id, 0)),
          );
        }
      }
      setOpenForm(false);
      setMessage(dispatch, {
        open: true,
        text: selected
          ? "Patrocinador atualizado com sucesso"
          : "Patrocinador criado com sucesso",
        severity: "success",
      });
      void load();
    } catch (error: unknown) {
      setMessage(dispatch, {
        open: true,
        text: error instanceof Error ? error.message : "Erro ao salvar patrocinador",
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LojaPageContainer
      title="Patrocinadores"
      subtitle="Gerencie os patrocinadores exibidos na Home e nos eventos."
      actions={
        <MDButton
          variant="gradient"
          color="dark"
          size="small"
          startIcon={<AddOutlinedIcon />}
          onClick={handleOpenCreate}
          sx={saveButtonSx}
        >
          Novo patrocinador
        </MDButton>
      }
    >
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatMini
            label={isFiltered ? "Resultados da busca" : "Total cadastrados"}
            value={isFiltered ? displayedList.length : list.length}
            accent
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Exibindo agora" value={displayedList.length} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Ativos" value={stats.ativos} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatMini label="Na Home" value={stats.naHome} />
        </Grid>
      </Grid>

      <MDBox sx={patrocinadoresToolbarSx}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <HandshakeOutlinedIcon sx={{ color: PATROCINADORES_ACCENT }} />
              <MDTypography variant="button" fontWeight="medium" color="text">
                {isFiltered
                  ? `${displayedList.length} patrocinador${displayedList.length === 1 ? "" : "es"} no filtro`
                  : `${list.length} patrocinador${list.length === 1 ? "" : "es"} cadastrado${list.length === 1 ? "" : "s"}`}
              </MDTypography>
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={patrocinadoresCompact}
                  onChange={handlePatrocinadoresCompact}
                  disabled={loadingSiteSettings}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: PATROCINADORES_ACCENT },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: PATROCINADORES_ACCENT,
                    },
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="caption" fontWeight={600} display="block">
                    Vitrine compacta
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {patrocinadoresCompact
                      ? "Somente logos na Home e eventos"
                      : "Cards completos com nome e descrição"}
                  </Typography>
                </Box>
              }
              sx={{ m: 0, alignItems: "flex-start" }}
            />
          </Stack>

          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel htmlFor="patrocinadores-search">Pesquisar patrocinador</InputLabel>
            <OutlinedInput
              id="patrocinadores-search"
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                debouncedSearch(e.target.value);
              }}
              label="Pesquisar patrocinador"
              placeholder="Nome ou descrição…"
              endAdornment={
                <InputAdornment position="end">
                  <Search fontSize="small" />
                </InputAdornment>
              }
              sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
            />
          </FormControl>
        </Stack>
      </MDBox>

      {!isLoading && displayedList.length === 0 ? (
        <MDBox
          py={6}
          textAlign="center"
          sx={{
            border: "1px dashed",
            borderColor: "grey.300",
            borderRadius: 2,
            bgcolor: "grey.50",
          }}
        >
          <HandshakeOutlinedIcon sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
          <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
            Nenhum patrocinador encontrado
          </MDTypography>
          <MDTypography variant="body2" color="text">
            {isFiltered
              ? "Ajuste a busca ou limpe o campo para ver todos."
              : "Cadastre o primeiro patrocinador para exibir no site."}
          </MDTypography>
        </MDBox>
      ) : (
        <TablePatrocinador
          rows={displayedList}
          loading={isLoading}
          onEdit={handleOpenEdit}
          onDelete={handleSelectDelete}
        />
      )}

      <PatrocinadorFormDialog
        open={openForm}
        selected={selected}
        form={form}
        logoPreview={logoPreview}
        fileInputRef={fileInputRef}
        eventosAtivos={eventosAtivos}
        selectedEventos={selectedEventos}
        loadingEventos={loadingEventos}
        isSubmitting={isSubmitting}
        onClose={handleCloseForm}
        onSubmit={() => void handleSubmit()}
        onFormChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))}
        onLogoChange={(e) => void handleLogoChange(e)}
        onSelectedEventosChange={setSelectedEventos}
      />

      <ProdutoImageCropModal
        open={cropModalOpen}
        file={cropFile}
        maxWidth={PATROCINADOR_LOGO_WIDTH}
        maxHeight={PATROCINADOR_LOGO_HEIGHT}
        title="Ajustar logo do patrocinador"
        onClose={resetCropState}
        onConfirm={handleCropConfirm}
        onError={handleCropError}
      />

      <MDDialog
        title="Excluir patrocinador"
        message={`Tem certeza que deseja excluir o patrocinador "${selected?.nome}"? Esta ação não pode ser desfeita.`}
        open={openConfirmDelete}
        onClose={() => setOpenConfirmDelete(false)}
        response={(resp: boolean) => {
          if (resp) {
            void handleConfirmDelete();
          } else {
            setOpenConfirmDelete(false);
          }
        }}
      />
    </LojaPageContainer>
  );
}
